import { IconButton, Link, Tooltip, Typography } from '@material-ui/core';
import { Add, Comment, Edit, LocationOn, Person } from '@material-ui/icons';
import { FormattedDate } from '@superdispatch/dates';
import {
  CardButton,
  ColorDynamic,
  Column,
  Columns,
  Inline,
  OverflowText,
  Stack,
} from '@superdispatch/ui';
import { Box, DescriptionItem, TextBox } from '@superdispatch/ui-lab';
import { useMemo, useState } from 'react';
import { CalendarMonthIcon } from 'shared/icons';
import { DeliveryAltIcon } from 'shared/icons/DeliveryAltIcon';
import { BusinessTypeTag } from 'shared/ui/BusinessTypeTag';
import { HiddenForPrint } from 'shared/ui/Print';
import { joinStrings } from 'shared/utils/StringUtils';
import styled from 'styled-components';
import { EditLoadDeliveryDrawer } from '../core/EditLoadDeliveryDrawer';
import { isEmptyLoadLeg, LoadDTO } from '../data/LoadDTO';
import { ViewLoadBusinessHours } from './ViewLoadBusinessHours';
import { ViewLoadLegNotes } from './ViewLoadLegNotes';
import { TooltipDescription } from './ViewLoadPickup';

interface ViewLoadDeliveryProps {
  load: LoadDTO;
  layout?: 'print';
}

const StyledDeliveryAltIcon = styled(DeliveryAltIcon)`
  path {
    fill: ${ColorDynamic.White};
  }
`;

export function ViewLoadDelivery({ load, layout }: ViewLoadDeliveryProps) {
  const { guid, delivery, delivery_driver_address } = load;
  const isEmpty = useMemo(() => isEmptyLoadLeg(delivery), [delivery]);
  const { venue, notes, actual_date, scheduled_date } = delivery;
  const [modalState, setModalState] = useState<'edit'>();

  const openDrawer = () => {
    setModalState('edit');
  };

  let canBeEdited = layout !== 'print' && load.can_be_edited;

  return (
    <>
      <EditLoadDeliveryDrawer
        loadGUID={guid}
        open={modalState === 'edit'}
        onClose={() => {
          setModalState(undefined);
        }}
      />

      {isEmpty ? (
        layout !== 'print' && (
          <HiddenForPrint>
            <CardButton
              startIcon={<Add />}
              onClick={openDrawer}
              disabled={!load.can_be_edited}
            >
              Add Delivery
            </CardButton>
          </HiddenForPrint>
        )
      ) : (
        <Stack>
          <Columns align="center" space="xsmall">
            <Column width="content">
              <StyledDeliveryAltIcon />
            </Column>

            <Column width="adaptive">
              {!venue.name ? (
                <Typography variant="h3" color="textSecondary">
                  No delivery name
                </Typography>
              ) : layout === 'print' ? (
                <Typography variant="h3">{venue.name}</Typography>
              ) : (
                <OverflowText variant="h3">{venue.name}</OverflowText>
              )}
            </Column>

            {canBeEdited && (
              <Column width="content">
                <IconButton
                  aria-label="Edit Delivery Button"
                  size="small"
                  onClick={openDrawer}
                >
                  <Edit />
                </IconButton>
              </Column>
            )}
          </Columns>

          <Box paddingLeft="xsmall">
            <Stack>
              <Inline space="xxsmall">
                {!!load.delivery.venue.business_type && (
                  <BusinessTypeTag
                    businessType={load.delivery.venue.business_type}
                  />
                )}
                <DescriptionItem
                  icon={
                    load.delivery.venue.business_type ? null : <LocationOn />
                  }
                  fallback="No address"
                  wrap={true}
                >
                  {joinStrings(
                    ', ',
                    venue.address,
                    venue.city,
                    `${venue.state} ${venue.zip}`,
                  )}
                </DescriptionItem>
              </Inline>

              <ViewLoadBusinessHours
                businessHoursType={delivery.business_hours_type}
                businessHours={delivery.business_hours}
              />

              <DescriptionItem
                aria-label="delivery scheduled due"
                icon={<CalendarMonthIcon />}
                label={scheduled_date ? 'Scheduled for' : undefined}
                fallback="No scheduled delivery date"
              >
                {!!scheduled_date && (
                  <FormattedDate date={scheduled_date} variant="Date" />
                )}
              </DescriptionItem>

              {!!actual_date && (
                <Inline verticalAlign="center" space="none">
                  <DescriptionItem inset={true} label="Delivered on">
                    <FormattedDate date={actual_date} variant="DateTime" />
                  </DescriptionItem>
                  {delivery_driver_address && (
                    <Tooltip
                      placement="top"
                      interactive={true}
                      title={
                        <>
                          <TextBox align="left" color="white" variant="body">
                            Delivered
                          </TextBox>
                          <br />
                          <TooltipDescription align="left" variant="body">
                            {delivery_driver_address}
                          </TooltipDescription>
                        </>
                      }
                    >
                      <LocationOn color="action" fontSize="small" />
                    </Tooltip>
                  )}
                </Inline>
              )}

              {venue.contacts.map((contact, idx) => (
                <Stack space="xxsmall" key={idx}>
                  <DescriptionItem icon={<Person />} fallback="No contact name">
                    {contact.name}
                  </DescriptionItem>

                  {!!contact.phone && (
                    <DescriptionItem inset={true} wrap={true}>
                      {contact.phone}
                    </DescriptionItem>
                  )}
                  {!!contact.phone2 && (
                    <DescriptionItem inset={true} wrap={true}>
                      {contact.phone2}
                    </DescriptionItem>
                  )}

                  {!!contact.email && (
                    <DescriptionItem inset={true}>
                      <Link href={`mailto:${contact.email}`}>
                        {contact.email}
                      </Link>
                    </DescriptionItem>
                  )}
                </Stack>
              ))}

              {!notes ? (
                <DescriptionItem
                  icon={<Comment />}
                  fallback="No delivery notes"
                />
              ) : (
                <>
                  <DescriptionItem icon={<Comment />} label="Delivery notes" />
                  <ViewLoadLegNotes
                    content={notes}
                    disableTruncate={layout === 'print'}
                  />
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      )}
    </>
  );
}
